/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heart, MessageSquare, Trash2, MoreHorizontal, Flag } from 'lucide-react';
import BookmarkButton from '@/components/BookmarkButton';
import { useNavigate } from 'react-router-dom';
import { CATEGORY_META, timeAgo, initials, extractYoutubeId } from '@/lib/utils-cat';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import ReportDialog from '@/components/ReportDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const CategoryBadge = ({ category, t }) => {
  const meta = CATEGORY_META[category];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1"
      style={{ backgroundColor: meta.soft, color: meta.textOnSoft, borderColor: meta.ring, boxShadow: `inset 0 0 0 1px ${meta.ring}` }}
      data-testid={`category-badge-${category}`}
    >
      <Icon className="h-3 w-3" />
      {t(meta.label)}
    </span>
  );
};

const YoutubeEmbed = ({ id }) => {
  if (!id) return null;
  return (
    <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black" data-testid="post-youtube-embed">
      <iframe
        title="YouTube video"
        className="absolute inset-0 w-full h-full"
        src={`https://www.youtube.com/embed/${id}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

export const PostCard = ({ post, onChanged, onDeleted, compact = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const author = post.author || {};
  const authorCat = author.category;
  const catMeta = CATEGORY_META[authorCat];
  const [liking, setLiking] = React.useState(false);
  const isOwner = user && author && user.id === author.id;

  const handleLike = async (e) => {
    e.stopPropagation();
    if (liking) return;
    setLiking(true);
    try {
      const res = await api.post(`/posts/${post.id}/like`);
      onChanged && onChanged({ ...post, liked_by_me: res.data.liked, like_count: res.data.like_count });
    } catch (err) {
      toast.error(t('errorOccurred'));
    } finally {
      setLiking(false);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await api.delete(`/posts/${post.id}`);
      onDeleted && onDeleted(post.id);
      toast.success(t('delete'));
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const ytId = post.youtube_id || extractYoutubeId(post.youtube_url);

  return (
    <Card
      data-testid="feed-post-card"
      className="p-4 md:p-5 rounded-2xl bg-white border border-border shadow-[0_1px_0_rgba(15,23,42,0.04),0_8px_20px_rgba(15,23,42,0.04)] hover:shadow-[0_1px_0_rgba(15,23,42,0.05),0_12px_28px_rgba(15,23,42,0.08)] transition-shadow cursor-pointer"
      onClick={() => !compact && navigate(`/posts/${post.id}`)}
    >
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          {author.avatar_base64 && <AvatarImage src={author.avatar_base64} />}
          <AvatarFallback className="text-sm font-semibold" style={{ backgroundColor: catMeta?.soft, color: catMeta?.textOnSoft }}>
            {initials(author.full_name)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-semibold text-sm truncate hover:underline"
              onClick={(e) => { e.stopPropagation(); if (author.id) navigate(`/profile/${author.id}`); }}
              data-testid="post-author-name"
            >
              {author.full_name || '—'}
            </span>
            <CategoryBadge category={authorCat} t={t} />
            {author.specialty && (
              <span className="text-xs text-muted-foreground truncate">· {author.specialty}</span>
            )}
          </div>
          <div className="text-[11px] text-muted-foreground">{timeAgo(post.created_at, t)}</div>
        </div>
        {isOwner ? (
          <button onClick={handleDelete} className="p-1.5 rounded-full text-muted-foreground hover:text-destructive hover:bg-destructive/10" data-testid="feed-post-delete-button">
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-full text-muted-foreground hover:bg-secondary" data-testid="feed-post-more-button">
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <ReportDialog targetType="post" targetId={post.id} trigger={
                <DropdownMenuItem onSelect={(e) => e.preventDefault()} data-testid="feed-post-report-item">
                  <Flag className="h-4 w-4 mr-2" /> {t('report')}
                </DropdownMenuItem>
              } />
              {user?.is_admin && (
                <DropdownMenuItem onClick={handleDelete} className="text-destructive" data-testid="feed-post-admin-delete-item">
                  <Trash2 className="h-4 w-4 mr-2" /> {t('delete')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{post.content}</div>

      {post.images_base64 && post.images_base64.length > 0 && (
        <div className={`mt-3 grid gap-2 ${post.images_base64.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
          {post.images_base64.slice(0, 4).map((img, idx) => (
            <div key={`${post.id}-img-${idx}`} className="aspect-video rounded-xl overflow-hidden bg-secondary" data-testid={`post-image-${idx}`}>
              <img src={img} alt={`post-media-${idx}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      )}

      {ytId && (
        <div className="mt-3">
          <YoutubeEmbed id={ytId} />
        </div>
      )}

      <div className="mt-3 flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className={`gap-1.5 ${post.liked_by_me ? 'text-primary' : ''}`}
          onClick={handleLike}
          data-testid="feed-post-like-button"
        >
          <Heart className={`h-4 w-4 ${post.liked_by_me ? 'fill-primary' : ''}`} />
          <span className="text-xs">{post.like_count || 0}</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5"
          onClick={(e) => { e.stopPropagation(); navigate(`/posts/${post.id}`); }}
          data-testid="feed-post-comment-button"
        >
          <MessageSquare className="h-4 w-4" />
          <span className="text-xs">{post.comment_count || 0}</span>
        </Button>
        <div className="ml-auto">
          <BookmarkButton targetType="post" targetId={post.id} initialSaved={post._bookmarked} size="sm" />
        </div>
      </div>
    </Card>
  );
};

export { CategoryBadge };
export default PostCard;
